import { Injectable } from '@angular/core';
import { AuthService } from '@telespot/web-core';
import { RolesStore } from '@telespot/authentication/ui';
import { AffiliatedUsersStore } from '../../../stores/user/affiliated-user.store';
import { UserMembershipsEffects } from '../../../stores/user/user-memberships.effects';
import { WorkspacesEffects } from '../../../stores/workspaces.effects';
import { BaseUserFacade } from '../base-user.facade';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Facade for the UserEditor component.
 * Coordinates AffiliatedUsersStore, RolesStore, and WorkspacesEffects for user editing flow.
 */
@Injectable()
export class UserEditorFacade extends BaseUserFacade {
  constructor(
    affiliatedUserStore: AffiliatedUsersStore,
    userMembershipsEffects: UserMembershipsEffects,
    rolesStore: RolesStore,
    workspacesEffects: WorkspacesEffects,
    authService: AuthService
  ) {
    super(affiliatedUserStore, userMembershipsEffects, rolesStore, workspacesEffects, authService);
    this.onSelectedUserChanged(this.affiliatedUserStore.userSelectionCompleted$);
  }

  // Editor-specific selectors
  public get selectedUser$() {
    return this.affiliatedUserStore.selectedUser$;
  }

  public get userLoadedForEdit$() {
    return this.affiliatedUserStore.userLoadedForEdit$;
  }

  public get selectedUserRoles$() {
    return this.affiliatedUserStore.selectedUserRoles$;
  }

  public get isDemoted$() {
    return this.affiliatedUserStore.isDemoted$;
  }

  public get loadingMemberships$() {
    return this.userMembershipsEffects.loadingMemberships$;
  }

  public readonly isSynced$ = this.select(
    this.affiliatedUserStore.rolesUpdated$,
    this.affiliatedUserStore.addedToOrganization$,
    this.userMembershipsEffects.addedToWorkspaces$,
    this.userMembershipsEffects.removedFromWorkspaces$,
    (rolesUpdated, addedToOrganization, addedToWorkspaces, removedFromWorkspaces) =>
      rolesUpdated && addedToOrganization && addedToWorkspaces && removedFromWorkspaces
  );

  // Editor-specific effects
  public readonly selectUser = (params: { userId: string; organizationId: string; maxUsers: number }) => {
    this.affiliatedUserStore.selectUser(params);
  };

  public readonly syncSelectedUser = () => {
    this.affiliatedUserStore.syncSelectedUser();
  };

  // Editor-specific updaters
  public readonly resetSelectedUserState = () => {
    this.affiliatedUserStore.resetSelectedUserState();
    this.userMembershipsEffects.resetMembershipState();
  };

  /**
   * Editor-specific initialization: does nothing specific.
   * User selection is handled separately via selectUser() called by the component.
   */
  protected initializeSpecific(): void {
    // No specific initialization needed for editor beyond the base initialization
    // The component will call selectUser() separately after initialization
  }

  private readonly onSelectedUserChanged = this.effect(
    (userSelection$: Observable<{ id: string; username: string; emailVerified: boolean }>) =>
      userSelection$.pipe(
        map((user) => {
          this.userMembershipsEffects.setActiveUser({
            clearMemberships: true,
            activeUser: {
              id: user.id,
              username: user.username,
              avatarUrl: undefined,
              emailVerified: user.emailVerified,
              membershipId: undefined,
            },
          });
          this.userMembershipsEffects.loadUserMemberships();
        })
      )
  );
}
